<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import phonePreviewFrame from './assets/phone-preview.svg';
import messageTopBg from './assets/message-top-tg.svg';

const props = defineProps({
  botName: {
    type: String,
    default: '',
  },
  avatarUrl: {
    type: String,
    default: '',
  },
  imageUrl: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  buttons: {
    type: Array,
    default: () => [],
  },
  showButtonClickBadges: {
    type: Boolean,
    default: false,
  },
  buttonClickCounts: {
    type: Object,
    default: () => ({}),
  },
  blocks: {
    type: Array,
    default: () => [],
  },
});

const previewBodyBg = '/tg-bot-campaign-preview-bg.svg';
const messageBottomBg = '/tg-message-bottom.svg';

const { t } = useI18n();

const normalizeType = type => {
  const normalized = (type || '')
    .toString()
    .trim()
    .toLowerCase()
    .replaceAll(' ', '_');
  if (normalized === 'photo') return 'image';
  if (normalized === 'document') return 'file';
  if (normalized === 'videonote') return 'video_note';
  return normalized || 'text';
};

const sanitizeButtons = buttons =>
  (Array.isArray(buttons) ? buttons : [])
    .map(button => ({
      text: button?.text?.toString().trim(),
      url: button?.url?.toString().trim(),
      trackingKey:
        button?.tracking_key?.toString().trim() ||
        button?.trackingKey?.toString().trim() ||
        '',
    }))
    .filter(button => button.text && button.url);

const previewMessage = computed(
  () =>
    props.message || t('CAMPAIGN.TELEGRAM.EDITOR.PREVIEW.MESSAGE_PLACEHOLDER')
);
const previewBotName = computed(
  () =>
    props.botName || t('CAMPAIGN.TELEGRAM.EDITOR.PREVIEW.BOT_NAME_PLACEHOLDER')
);

const hasLegacyPrimary = computed(() => {
  const hasMessage = Boolean((props.message || '').toString().trim());
  const hasImage = Boolean((props.imageUrl || '').toString().trim());
  const hasButtons = sanitizeButtons(props.buttons).length > 0;
  return hasMessage || hasImage || hasButtons;
});

const normalizedBlocksFromProps = computed(() =>
  (Array.isArray(props.blocks) ? props.blocks : []).map((rawBlock, index) => {
    const type = normalizeType(rawBlock?.type);
    const baseBlock = {
      id: rawBlock?.id || `block-${index}`,
      type,
      text: rawBlock?.text || rawBlock?.caption || '',
      mediaUrl:
        rawBlock?.media_url || rawBlock?.mediaUrl || rawBlock?.image_url || '',
      buttons: sanitizeButtons(rawBlock?.buttons),
      items: [],
      cards: [],
      starCount: Number(rawBlock?.star_count || rawBlock?.starCount || 0),
    };

    if (type === 'media_group') {
      baseBlock.items = (
        Array.isArray(rawBlock?.items) ? rawBlock.items : []
      ).map(item => ({
        type: normalizeType(item?.type || 'image'),
        mediaUrl: item?.media_url || item?.mediaUrl || item?.media || '',
        caption: item?.caption || '',
      }));
      baseBlock.text = rawBlock?.follow_up_text || rawBlock?.followUpText || '';
    }

    if (type === 'carousel') {
      baseBlock.cards = (
        Array.isArray(rawBlock?.cards) ? rawBlock.cards : []
      ).map(card => ({
        mediaType: normalizeType(
          card?.media_type || card?.mediaType || card?.type
        ),
        mediaUrl: card?.media_url || card?.mediaUrl || card?.media || '',
        caption: card?.caption || card?.text || '',
        buttons: sanitizeButtons(card?.buttons),
      }));
      baseBlock.text =
        rawBlock?.text ||
        `Carousel (${baseBlock.cards.length || 0} card${baseBlock.cards.length === 1 ? '' : 's'})`;
    }

    if (type === 'paid_media') {
      baseBlock.items = (
        Array.isArray(rawBlock?.items) ? rawBlock.items : []
      ).map(item => ({
        type: normalizeType(item?.type),
        mediaUrl: item?.media_url || item?.mediaUrl || item?.media || '',
      }));
      baseBlock.text = rawBlock?.caption || rawBlock?.text || '';
    }

    return baseBlock;
  })
);

const primaryBlock = computed(() => {
  if (hasLegacyPrimary.value) {
    return {
      id: 'primary',
      type: props.imageUrl ? 'image' : 'text',
      mediaUrl: props.imageUrl || '',
      text: (props.message || '').trim() || previewMessage.value,
      buttons: sanitizeButtons(props.buttons),
    };
  }

  const primaryFromBlocks = normalizedBlocksFromProps.value[0];
  if (primaryFromBlocks) return primaryFromBlocks;

  return {
    id: 'primary',
    type: 'text',
    mediaUrl: '',
    text: previewMessage.value,
    buttons: [],
    items: [],
    cards: [],
    starCount: 0,
  };
});

const normalizedAdditionalBlocks = computed(() =>
  hasLegacyPrimary.value
    ? normalizedBlocksFromProps.value
    : normalizedBlocksFromProps.value.slice(1)
);

const previewBlocks = computed(() => [
  primaryBlock.value,
  ...normalizedAdditionalBlocks.value,
]);

const getBlockMessage = block => {
  if (block.type === 'text') return block.text || previewMessage.value;
  if (block.type === 'video') return block.text || '[Video]';
  if (block.type === 'file') return block.text || '[File]';
  if (block.type === 'audio') return block.text || '[Audio]';
  if (block.type === 'video_note') return block.text || '[Video note]';
  if (block.type === 'paid_media') return block.text || 'Paid media';
  return block.text || '';
};

const firstCarouselCard = block =>
  Array.isArray(block.cards) && block.cards.length ? block.cards[0] : null;

const blockTypeLabel = type => {
  const normalized = normalizeType(type);
  if (normalized === 'image') return 'Image';
  if (normalized === 'video') return 'Video';
  if (normalized === 'file') return 'File';
  if (normalized === 'audio') return 'Audio';
  if (normalized === 'video_note') return 'Video note';
  if (normalized === 'media_group') return 'Media group';
  if (normalized === 'carousel') return 'Carousel';
  if (normalized === 'paid_media') return 'Paid media';
  return 'Text';
};

const buttonClickCount = button =>
  Number(props.buttonClickCounts?.[button.trackingKey] || 0);
</script>

<template>
  <div class="telegram-preview-wrap">
    <div class="telegram-preview-phone">
      <div class="telegram-preview-content">
        <div
          class="telegram-preview-heading"
          :style="{ backgroundImage: `url(${messageTopBg})` }"
        >
          <span class="telegram-preview-back-icon i-ph-arrow-left-bold" />
          <img
            v-if="avatarUrl"
            :src="avatarUrl"
            class="telegram-preview-avatar"
            alt="bot avatar"
          />
          <div
            v-else
            class="telegram-preview-avatar telegram-preview-avatar-fallback"
          >
            {{ previewBotName[0] }}
          </div>

          <div class="telegram-preview-name">
            {{ previewBotName }}
            <span class="telegram-preview-type">
              {{ t('CAMPAIGN.TELEGRAM.EDITOR.PREVIEW.BOT_LABEL') }}
            </span>
          </div>
        </div>

        <div
          class="telegram-preview-body"
          :style="{ backgroundImage: `url(${previewBodyBg})` }"
        >
          <div
            v-for="(block, blockIndex) in previewBlocks"
            :key="`${block.id}-${blockIndex}`"
            class="telegram-preview-group"
          >
            <div
              v-if="previewBlocks.length > 1"
              class="telegram-preview-block-tag"
            >
              Post {{ blockIndex + 1 }}
            </div>

            <div
              v-if="block.type === 'image' && block.mediaUrl"
              class="telegram-preview-image-wrap"
            >
              <img
                :src="block.mediaUrl"
                class="telegram-preview-image"
                alt="campaign image"
              />
              <div class="telegram-preview-caption">
                {{ getBlockMessage(block) }}
              </div>
            </div>

            <template v-else-if="block.type === 'media_group'">
              <div class="telegram-preview-media-group">
                <div class="telegram-preview-media-grid">
                  <div
                    v-for="(item, itemIndex) in block.items.slice(0, 4)"
                    :key="`media-item-${itemIndex}`"
                    class="telegram-preview-media-cell"
                  >
                    <img
                      v-if="item.type === 'image' && item.mediaUrl"
                      :src="item.mediaUrl"
                      class="telegram-preview-media-image"
                      alt="media item"
                    />
                    <div v-else class="telegram-preview-media-placeholder">
                      {{ blockTypeLabel(item.type) }}
                    </div>
                  </div>
                </div>
                <div class="telegram-preview-caption">
                  Album • {{ block.items.length }} item(s)
                </div>
              </div>
              <div v-if="block.text" class="telegram-preview-message">
                {{ block.text }}
              </div>
            </template>

            <template v-else-if="block.type === 'carousel'">
              <div class="telegram-preview-message">
                {{ block.text }}
              </div>
              <div
                v-if="
                  firstCarouselCard(block) && firstCarouselCard(block).mediaUrl
                "
                class="telegram-preview-image-wrap"
              >
                <img
                  v-if="firstCarouselCard(block).mediaType === 'image'"
                  :src="firstCarouselCard(block).mediaUrl"
                  class="telegram-preview-image"
                  alt="carousel card"
                />
                <div v-else class="telegram-preview-media-placeholder is-large">
                  {{ blockTypeLabel(firstCarouselCard(block).mediaType) }}
                </div>
                <div class="telegram-preview-caption">
                  {{
                    firstCarouselCard(block).caption ||
                    `Card 1 of ${block.cards.length}`
                  }}
                </div>
              </div>
              <div class="telegram-preview-carousel-nav">
                <span>◀</span>
                <span>1 / {{ block.cards.length }}</span>
                <span>▶</span>
              </div>
            </template>

            <div v-else class="telegram-preview-message">
              <span
                v-if="block.type !== 'text'"
                class="telegram-preview-media-type-label"
              >
                {{ blockTypeLabel(block.type) }}
              </span>
              <template v-if="block.type === 'paid_media'">
                {{ getBlockMessage(block) || 'Premium content' }} · ⭐
                {{ block.starCount || 1 }} · {{ block.items.length }} item(s)
              </template>
              <template v-else>
                {{ getBlockMessage(block) }}
              </template>
            </div>

            <div
              v-if="sanitizeButtons(block.buttons).length"
              class="telegram-preview-buttons"
              :class="{
                'one-column': sanitizeButtons(block.buttons).length === 1,
              }"
            >
              <a
                v-for="button in sanitizeButtons(block.buttons)"
                :key="`${button.text}-${button.url}`"
                :href="button.url"
                target="_blank"
                rel="noopener noreferrer"
                class="telegram-preview-button"
              >
                <span>{{ button.text }}</span>
                <span
                  v-if="
                    showButtonClickBadges &&
                    button.trackingKey &&
                    buttonClickCount(button) > 0
                  "
                  class="telegram-preview-button-click-badge"
                >
                  {{ buttonClickCount(button) }}
                </span>
              </a>
            </div>
          </div>
        </div>

        <div
          class="telegram-preview-foot"
          :style="{ backgroundImage: `url(${messageBottomBg})` }"
        />
      </div>
      <img
        :src="phonePreviewFrame"
        class="telegram-preview-frame"
        alt="phone frame"
      />
    </div>
  </div>
</template>

<style scoped>
.telegram-preview-wrap {
  padding-top: 1.5rem;
  display: flex;
  justify-content: center;
}

.telegram-preview-phone {
  width: 288px;
  height: 595px;
  position: relative;
  color: #fff;
}

.telegram-preview-frame {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  filter: drop-shadow(0 10px 30px rgba(0, 0, 0, 0.3));
}

.telegram-preview-content {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px;
  bottom: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 38px;
  background: #000000;
  z-index: 2;
}

.telegram-preview-heading {
  position: relative;
  height: 50px;
  width: 100%;
  background-repeat: no-repeat;
  background-position: top center;
  background-size: cover;
  border-radius: 28px 28px 0 0;
  flex-shrink: 0;
}

.telegram-preview-back-icon {
  position: absolute;
  left: 12px;
  top: 15px;
  color: #6a6f79;
  font-size: 14px;
  line-height: 1;
  z-index: 3;
  pointer-events: none;
}

.telegram-preview-avatar {
  position: absolute;
  top: 15px;
  right: 16px;
  width: 23px;
  height: 23px;
  border-radius: 50%;
  object-fit: cover;
  z-index: 5;
}

.telegram-preview-avatar-fallback {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #646464;
  color: #ffffff;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
}

.telegram-preview-name {
  position: absolute;
  left: 24px;
  right: 44px;
  top: 14px;
  text-align: center;
  font-size: 0.6875rem;
  font-weight: 700;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #ffffff;
  z-index: 2;
}

.telegram-preview-type {
  display: block;
  margin-top: 0.125rem;
  font-size: 0.625rem;
  font-weight: 400;
  opacity: 0.65;
}

.telegram-preview-body {
  flex-grow: 1;
  width: 100%;
  padding: 0.75rem 0.75rem 1.25rem;
  overflow-y: auto;
  background-color: #0f1012;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
}

.telegram-preview-body::-webkit-scrollbar {
  width: 4px;
}

.telegram-preview-body::-webkit-scrollbar-track {
  background: transparent;
}

.telegram-preview-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.telegram-preview-group {
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
}

.telegram-preview-block-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.125rem 0.5rem;
  margin-bottom: 0.375rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #c7c9d1;
  background: #1f2023;
}

.telegram-preview-image-wrap {
  border-radius: 10px;
  overflow: hidden;
  background: #282730;
  margin-bottom: 2px;
}

.telegram-preview-image {
  width: 100%;
  height: 132px;
  object-fit: cover;
  display: block;
}

.telegram-preview-caption {
  padding: 0.5rem 0.625rem;
  font-size: 0.875rem;
  line-height: 1.25;
  white-space: pre-wrap;
  word-break: break-word;
  background: #282730;
  color: #ffffff;
}

.telegram-preview-message {
  padding: 0.625rem 0.75rem;
  background: #282730;
  border-radius: 14px;
  font-size: 0.875rem;
  line-height: 1.25;
  white-space: pre-wrap;
  word-break: break-word;
  color: #ffffff;
  margin-bottom: 2px;
}

.telegram-preview-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin-top: 2px;
}

.telegram-preview-buttons.one-column {
  flex-direction: column;
}

.telegram-preview-buttons.one-column .telegram-preview-button {
  flex-basis: 100%;
  min-width: 100%;
}

.telegram-preview-button {
  position: relative;
  flex: 1 1 calc(50% - 1px);
  min-width: calc(50% - 1px);
  padding: 0.5rem 1.25rem 0.5rem 0.625rem;
  border-radius: 10px;
  background: #262628;
  color: #ffffff;
  text-decoration: none;
  text-align: center;
  font-size: 0.8125rem;
  line-height: 1.3;
  font-weight: 500;
  transition: background-color 0.15s ease;
}

.telegram-preview-button::after {
  content: '↗';
  position: absolute;
  top: 50%;
  right: 8px;
  font-size: 11px;
  line-height: 1;
  color: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%);
}

.telegram-preview-button:hover {
  background: #2f2f31;
}

.telegram-preview-button span {
  display: block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}

.telegram-preview-button-click-badge {
  position: absolute;
  right: 2px;
  top: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 9999px;
  background: #ef4444;
  color: #ffffff;
  font-size: 10px;
  line-height: 16px;
  font-weight: 700;
  text-align: center;
}

.telegram-preview-media-type-label {
  display: inline-flex;
  margin-right: 0.25rem;
  color: #7cc4ff;
  font-size: 0.75rem;
  font-weight: 600;
}

.telegram-preview-media-group {
  border-radius: 10px;
  overflow: hidden;
  background: #282730;
  margin-bottom: 2px;
}

.telegram-preview-media-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2px;
  padding: 2px;
}

.telegram-preview-media-cell {
  min-height: 52px;
  background: #1d1f24;
  border-radius: 8px;
  overflow: hidden;
}

.telegram-preview-media-image {
  width: 100%;
  height: 100%;
  min-height: 52px;
  object-fit: cover;
  display: block;
}

.telegram-preview-media-placeholder {
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  font-size: 0.6875rem;
  font-weight: 600;
  color: #bbc2d2;
  background: #1a1c21;
  text-align: center;
}

.telegram-preview-media-placeholder.is-large {
  min-height: 132px;
}

.telegram-preview-carousel-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
  background: #1f2023;
  color: #c5c9d5;
  font-size: 0.6875rem;
}

.telegram-preview-foot {
  height: 50px;
  background-repeat: no-repeat;
  background-position: center top;
  background-size: cover;
  flex-shrink: 0;
}
</style>
